/**
 * mix-analyzer-engine.js — window.MixAnalyzer
 *
 * The diagnosis + advice brain behind /mix-analyzer. Given YOUR track and a
 * REFERENCE track it produces (a) a full metric bundle per track and (b) a
 * RANKED list of plain-language, DAW-actionable recommendations for making
 * your mix sound closer to the reference.
 *
 * Why this exists (competitor sweep 2026-07-15): every serious tool — iZotope
 * Ozone/Tonal Balance Control, Youlean, Voxengo SPAN, Nugen MasterCheck/
 * Visualizer, Waves PAZ/WLM, Melda MAnalyzer, Matchering — is either a METER
 * (shows numbers/graphs, you interpret) or an AUTO-PROCESSOR (does it for you
 * inside its own EQ). None translate a measured delta-vs-reference into
 * explicit sentences like "your low end is +3 dB heavy — pull a low shelf
 * around 90 Hz". That translation is this file's whole job.
 *
 * Pure measurement layer — no DOM, no i18n. Every recommendation is a
 * { axis, severity, key, args } record; the blade maps `key` → a localized
 * string with `args`. That keeps the DSP testable and the wording in 9 lang
 * files. Reuses the platform's validated DSP wholesale:
 *   - LoudnessR128.measureDetailed        (BS.1770-4 LUFS / LRA / true-peak)
 *   - AudioComparatorEngine.basicStats / computeStereoMetrics /
 *     computeAveragePsd / findSpectralCutoff
 *   - MasteringReference.analyze          (9-band tonal balance + width vs ref)
 *
 * Thresholds are sourced, not invented:
 *   - PLR (truePeak − integratedLUFS) healthy 8–15, <8 "squashed" — Nugen
 *     MasterCheck's published rule of thumb.
 *   - Master ceiling −1 dBTP (−3 for MP3-bound) — universal streaming guidance.
 *   - Per-band advice clamped ±6 dB — matches MasteringReference's own clamp.
 *   - Clipping: ≥8 near-full-scale samples flags, heavy above ~0.05 % — the
 *     Matchering clip/limited thresholds (8 / 128 samples).
 */
(function () {
    'use strict';

    // 9 perceptual band centers — identical to MasteringReference.CENTERS so the
    // per-band advice and the tonal-match engine speak the same axis.
    var BANDS = [
        { freq: 60,    key: 'sub' },
        { freq: 120,   key: 'bass' },
        { freq: 250,   key: 'lowmid' },
        { freq: 500,   key: 'body' },
        { freq: 1000,  key: 'mid' },
        { freq: 2000,  key: 'upmid' },
        { freq: 4000,  key: 'presence' },
        { freq: 8000,  key: 'brilliance' },
        { freq: 12000, key: 'air' }
    ];

    // 4 broad zones — the split every competitor standardized on (iZotope TBC
    // Broad view). Used for the one-glance summary; the 9 bands drive the detail.
    var ZONES = [
        { key: 'low',    lo: 20,   hi: 250,   bands: [0, 1] },
        { key: 'lowmid', lo: 250,  hi: 2000,  bands: [2, 3, 4] },
        { key: 'himid',  lo: 2000, hi: 8000,  bands: [5, 6] },
        { key: 'high',   lo: 8000, hi: 20000, bands: [7, 8] }
    ];

    // Genre reference targets — for users WITHOUT a reference track of their own.
    // bandsDb = the tonal SHAPE (each of the 9 bands relative to the track's own
    // spectral median, in dB); lufs/plr/lra = loudness & dynamics anchors; width
    // = a side/mid width fraction hint. These are informed mastering ballparks
    // (not a proprietary trained corpus — labelled as "guideline targets" in the
    // UI), aligned to common streaming/genre practice. Compared exactly like a
    // real reference, so the same numbered advice engine drives them.
    var GENRE_TARGETS = {
        edm:       { lufs: -9,  plr: 7,  lra: 4,  bandsDb: [3, 2, -1, -1, 0, 1, 2, 2, 3],   width: 0.28 },
        hiphop:    { lufs: -9,  plr: 7,  lra: 4,  bandsDb: [4, 3, 0, -1, -1, 0, 1, 1, 1],    width: 0.20 },
        pop:       { lufs: -10, plr: 8,  lra: 5,  bandsDb: [1, 1, -1, 0, 0, 1, 2, 2, 2],     width: 0.22 },
        rock:      { lufs: -10, plr: 9,  lra: 6,  bandsDb: [1, 1, 0, 1, 1, 1, 1, 0, -1],     width: 0.20 },
        acoustic:  { lufs: -14, plr: 12, lra: 9,  bandsDb: [0, 1, 0, 1, 1, 1, 1, 1, 1],      width: 0.18 },
        jazz:      { lufs: -16, plr: 14, lra: 11, bandsDb: [1, 1, 1, 1, 0, 0, 0, 1, 1],      width: 0.18 },
        classical: { lufs: -20, plr: 16, lra: 15, bandsDb: [0, 1, 1, 1, 0, 0, 0, 1, 2],      width: 0.16 }
    };

    function db(x) { return (x > 0) ? 20 * Math.log10(x) : -Infinity; }

    // Mono down-mix (mid) for spectral work.
    function midChannel(buffer) {
        var n = buffer.length;
        if (buffer.numberOfChannels < 2) return buffer.getChannelData(0);
        var L = buffer.getChannelData(0), R = buffer.getChannelData(1);
        var m = new Float32Array(n);
        for (var i = 0; i < n; i++) m[i] = (L[i] + R[i]) * 0.5;
        return m;
    }

    /**
     * Bass mono-compatibility: L/R phase correlation of the low band only.
     * A 2nd-order Butterworth low-pass (Q≈0.707) at `cutoff` is run over L and
     * R, then their normalized cross-correlation is taken. On club systems and
     * phone speakers the sub is summed to mono, so anti-phase low end partly
     * cancels — corr near 1 = safe, <0.5 = trouble, <0 = one channel is
     * inverted in the bass. Mono files return 1 (nothing to cancel).
     */
    function bassCorrelation(buffer, cutoff) {
        if (buffer.numberOfChannels < 2) return 1;
        var sr = buffer.sampleRate;
        var L = buffer.getChannelData(0), R = buffer.getChannelData(1);
        var n = Math.min(L.length, R.length);
        if (n < 16) return 1;

        // RBJ low-pass biquad coefficients.
        var w0 = 2 * Math.PI * cutoff / sr;
        var cw = Math.cos(w0), sw = Math.sin(w0);
        var alpha = sw / (2 * 0.70710678);
        var b0 = (1 - cw) / 2, b1 = 1 - cw, b2 = (1 - cw) / 2;
        var a0 = 1 + alpha, a1 = -2 * cw, a2 = 1 - alpha;
        b0 /= a0; b1 /= a0; b2 /= a0; a1 /= a0; a2 /= a0;

        var lx1 = 0, lx2 = 0, ly1 = 0, ly2 = 0;
        var rx1 = 0, rx2 = 0, ry1 = 0, ry2 = 0;
        var sLR = 0, sLL = 0, sRR = 0, sL = 0, sR = 0;
        var lf = new Float32Array(n), rf = new Float32Array(n);
        var i, x, y;
        for (i = 0; i < n; i++) {
            x = L[i]; y = b0 * x + b1 * lx1 + b2 * lx2 - a1 * ly1 - a2 * ly2;
            lx2 = lx1; lx1 = x; ly2 = ly1; ly1 = y; lf[i] = y; sL += y;
            x = R[i]; y = b0 * x + b1 * rx1 + b2 * rx2 - a1 * ry1 - a2 * ry2;
            rx2 = rx1; rx1 = x; ry2 = ry1; ry1 = y; rf[i] = y; sR += y;
        }
        var mL = sL / n, mR = sR / n;
        for (i = 0; i < n; i++) {
            var l = lf[i] - mL, r = rf[i] - mR;
            sLR += l * r; sLL += l * l; sRR += r * r;
        }
        var denom = Math.sqrt(sLL * sRR);
        return denom > 0 ? (sLR / denom) : 1;
    }

    /**
     * Clipping + DC offset in one pass (true-peak comes from LoudnessR128).
     * Clipping counted at |sample| ≥ 0.997 (the level a normalized brickwall
     * master sits at). DC = channel mean; anything past a few thousandths eats
     * headroom and signals a missing high-pass.
     */
    function techStats(buffer) {
        var ch = buffer.numberOfChannels, len = buffer.length;
        var clip = 0, dcMax = 0, total = 0;
        for (var c = 0; c < ch; c++) {
            var d = buffer.getChannelData(c), sum = 0;
            for (var i = 0; i < len; i++) {
                var v = d[i];
                if (v >= 0.997 || v <= -0.997) clip++;
                sum += v;
            }
            var dc = Math.abs(sum / len);
            if (dc > dcMax) dcMax = dc;
            total += len;
        }
        return { clipCount: clip, clipPct: total > 0 ? (clip / total) * 100 : 0, dcMax: dcMax };
    }

    // Band levels (dB) from a raw MasteringReference.bandLevels() vector,
    // normalized to the vector's own mean so we compare tonal SHAPE not level.
    function normalizedBandsDb(raw) {
        var mean = 0, i;
        for (i = 0; i < raw.length; i++) mean += raw[i];
        mean = (mean / raw.length) || 1e-9;
        var out = new Array(raw.length);
        for (i = 0; i < raw.length; i++) out[i] = db(raw[i] / mean);
        return out;
    }

    // Same, but normalized to the MEDIAN band — robust to a single outlier band
    // (one boomy low end shouldn't shift the whole reference level and make every
    // other band read as "too quiet"). Used for the compare-vs-reference deltas.
    function normalizedBandsDbMedian(raw) {
        var s = Array.prototype.slice.call(raw).sort(function (a, b) { return a - b; });
        var med = s[s.length >> 1] || 1e-9;
        var out = new Array(raw.length);
        for (var i = 0; i < raw.length; i++) out[i] = db(raw[i] / med);
        return out;
    }

    /**
     * Full metric bundle for one decoded AudioBuffer. Everything downstream
     * (recommendations, meter table, charts) reads from this object.
     */
    function analyze(buffer) {
        var sr = buffer.sampleRate;
        var det = (window.LoudnessR128 && LoudnessR128.measureDetailed)
            ? LoudnessR128.measureDetailed(buffer)
            : { integratedLUFS: -Infinity, shortTermMaxLUFS: -Infinity, momentaryMaxLUFS: -Infinity, loudnessRangeLU: 0, peakDb: -Infinity, rmsDb: -Infinity, truePeakDb: -Infinity };
        var basic = (window.AudioComparatorEngine && AudioComparatorEngine.basicStats)
            ? AudioComparatorEngine.basicStats(buffer) : null;
        var stereo = (window.AudioComparatorEngine && AudioComparatorEngine.computeStereoMetrics)
            ? AudioComparatorEngine.computeStereoMetrics(buffer) : null;
        var psd = (window.AudioComparatorEngine && AudioComparatorEngine.computeAveragePsd)
            ? AudioComparatorEngine.computeAveragePsd(buffer, { fftSize: 8192 }) : null;
        var cutoff = (psd && AudioComparatorEngine.findSpectralCutoff)
            ? AudioComparatorEngine.findSpectralCutoff(psd) : null;
        var rawBands = (window.MasteringReference && MasteringReference.bandLevels)
            ? MasteringReference.bandLevels(midChannel(buffer), sr) : null;
        var tech = techStats(buffer);

        var lufs = det.integratedLUFS;
        var tp = det.truePeakDb;
        // PLR = true-peak − integrated loudness (whole-track dynamics headroom).
        var plr = (isFinite(tp) && isFinite(lufs)) ? (tp - lufs) : NaN;
        // Crest = peak − RMS (dB), micro-dynamics on the raw signal.
        var crestDb = (basic && isFinite(basic.peakDbfs) && isFinite(basic.rmsDbfs))
            ? (basic.peakDbfs - basic.rmsDbfs) : NaN;

        return {
            duration: buffer.duration,
            sampleRate: sr,
            channels: buffer.numberOfChannels,
            lufs: lufs,
            shortTermMax: det.shortTermMaxLUFS,
            momentaryMax: det.momentaryMaxLUFS,
            lra: det.loudnessRangeLU,
            truePeak: tp,
            peakDb: det.peakDb,
            rmsDb: det.rmsDb,
            plr: plr,
            crestDb: crestDb,
            stereo: stereo,
            bassCorr: bassCorrelation(buffer, 150),
            psd: psd,
            cutoff: cutoff,
            bandsRaw: rawBands,
            bandsDb: rawBands ? normalizedBandsDb(rawBands) : null,
            tech: tech
        };
    }

    // Severity ranks for sorting (major first).
    var SEV = { major: 2, minor: 1, ok: 0 };

    // Normalize any target into one shape compare() can read, so a genre profile,
    // a single reference file, or an average of several references all drive the
    // SAME advice engine. Returns median-normalized band dB + loudness/dynamics/
    // width anchors. `isGenre` suppresses reference-only checks (e.g. ref_lossy).
    function targetView(target) {
        if (!target) return null;
        if (target.isGenre) {
            return { bandsDbMedian: target.bandsDb, lufs: target.lufs, plr: target.plr,
                lra: target.lra, width: target.width, truePeak: -1, cutoff: null,
                isGenre: true, isMono: false, corr: null, label: target.key };
        }
        // a reference file bundle (or an averaged one) from analyze()
        return {
            bandsDbMedian: target.bandsRaw ? normalizedBandsDbMedian(target.bandsRaw) : null,
            lufs: target.lufs, plr: target.plr, lra: target.lra,
            width: (target.stereo && !target.stereo.isMono) ? target.stereo.width : null,
            truePeak: target.truePeak, cutoff: target.cutoff, isGenre: false,
            isMono: !!(target.stereo && target.stereo.isMono),
            corr: target.stereo ? target.stereo.phaseCorrelation : null, label: null
        };
    }

    // Average several analyze() bundles into one reference target — a stable
    // aim from 2-3 tracks (ADPTR/Sonible let you stack references for exactly
    // this). Band levels averaged element-wise; loudness/dynamics/stereo meaned.
    function averageTargets(arr) {
        arr = (arr || []).filter(function (m) { return m && m.bandsRaw; });
        if (!arr.length) return null;
        if (arr.length === 1) return arr[0];
        var n = arr.length, bands = new Array(BANDS.length);
        for (var i = 0; i < BANDS.length; i++) {
            var s = 0; for (var k = 0; k < n; k++) s += (arr[k].bandsRaw[i] || 0);
            bands[i] = s / n;
        }
        function mean(f) { var s = 0, c = 0; arr.forEach(function (m) { var v = f(m); if (isFinite(v)) { s += v; c++; } }); return c ? s / c : NaN; }
        return {
            bandsRaw: bands, channels: 2,
            lufs: mean(function (m) { return m.lufs; }), plr: mean(function (m) { return m.plr; }),
            lra: mean(function (m) { return m.lra; }), truePeak: mean(function (m) { return m.truePeak; }),
            crestDb: mean(function (m) { return m.crestDb; }),
            stereo: { isMono: false, msRatio: 1,
                width: mean(function (m) { return (m.stereo && !m.stereo.isMono) ? m.stereo.width : NaN; }),
                phaseCorrelation: mean(function (m) { return m.stereo ? m.stereo.phaseCorrelation : NaN; }) },
            cutoff: null, _avgCount: n
        };
    }

    /**
     * Compare mine vs reference → ranked recommendations + table rows.
     *
     * `mine` and `ref` are analyze() bundles. Returns:
     *   {
     *     recommendations: [{ axis, severity, key, args, weight }],  // ranked
     *     zoneDeltas:  [{ key, deltaDb }],   // 4 broad tonal zones (ref − mine)
     *     bandDeltas:  [{ freq, key, deltaDb, dir }],  // 9 bands, clamped ±6
     *     widthFactor,                       // MasteringReference width ratio
     *     summary: { major, minor, verdict } // counts + 'close'|'minor'|'major'
     *   }
     */
    function compare(mine, target) {
        // `target` is a reference bundle (single or averaged) OR a genre profile.
        // targetView flattens all three to one shape so the advice engine is shared.
        var tv = targetView(target) || {};
        var recs = [];
        function push(axis, severity, key, args, weight) {
            recs.push({ axis: axis, severity: severity, key: key, args: args || {}, weight: weight || 0 });
        }

        // ── Tonal balance (9 bands) ──────────────────────────────────────────
        // Delta = (ref band level) − (yours), each spectrum normalized to its own
        // MEDIAN band so a single fat band shows up as ONE outlier to fix rather
        // than smearing into "lift the other eight". (Mean-normalization — what
        // the Matchering-style matcher uses to *apply* a curve — re-expresses a
        // +9 dB low boom as small boosts everywhere; wrong for human advice.)
        // + delta => the reference is denser here => boost yours. Clamp ±6 dB to
        // match the mastering matcher's own move ceiling.
        var bandDeltas = [], zoneDeltas = [], widthFactor = 1;
        if (mine.bandsRaw && tv.bandsDbMedian && mine.bandsRaw.length === tv.bandsDbMedian.length) {
            var mineN = normalizedBandsDbMedian(mine.bandsRaw);
            var refN = tv.bandsDbMedian; // already median-normalized (genre profile or reference file)
            var byIndex = {};
            for (var i = 0; i < BANDS.length; i++) {
                var d = refN[i] - mineN[i];
                if (d > 6) d = 6; else if (d < -6) d = -6;
                byIndex[i] = d;
                bandDeltas.push({ freq: BANDS[i].freq, key: BANDS[i].key, deltaDb: d, dir: d >= 0 ? 'boost' : 'cut' });
            }
            if (mine.stereo && !tv.isGenre && target.stereo && mine.stereo.msRatio > 0 && isFinite(target.stereo.msRatio)) {
                widthFactor = target.stereo.msRatio > 0 ? (mine.stereo.msRatio / target.stereo.msRatio) : 1;
            }
            // Aggregate the 9 bands into the 4 broad zones for the summary bars.
            ZONES.forEach(function (z) {
                var s = 0, c = 0;
                z.bands.forEach(function (bi) { if (byIndex[bi] != null) { s += byIndex[bi]; c++; } });
                zoneDeltas.push({ key: z.key, deltaDb: c ? s / c : 0 });
            });
            // Emit a recommendation per off-balance band (>1.5 dB), capped at the
            // 3 STRONGEST moves so the priority actions aren't buried. The 4 zone
            // bars below still show the full tonal picture. ≥4 dB is a major move.
            bandDeltas
                .filter(function (bd) { return Math.abs(bd.deltaDb) >= 1.5; })
                .sort(function (a, b) { return Math.abs(b.deltaDb) - Math.abs(a.deltaDb); })
                .slice(0, 3)
                .forEach(function (bd) {
                    var a = Math.abs(bd.deltaDb);
                    var sev = a >= 4 ? 'major' : 'minor';
                    push('tonal', sev, bd.dir === 'boost' ? 'band_boost' : 'band_cut',
                        { band: bd.key, freq: bd.freq, db: a.toFixed(1) }, a);
                });
        }

        // ── Loudness ─────────────────────────────────────────────────────────
        if (isFinite(mine.lufs) && isFinite(tv.lufs)) {
            var dLufs = tv.lufs - mine.lufs; // + => target louder
            if (Math.abs(dLufs) >= 1) {
                var sevL = Math.abs(dLufs) >= 4 ? 'major' : 'minor';
                push('loudness', sevL, dLufs > 0 ? 'loud_up' : 'loud_down',
                    { mine: mine.lufs.toFixed(1), ref: tv.lufs.toFixed(1), db: Math.abs(dLufs).toFixed(1) },
                    Math.abs(dLufs));
            }
        }

        // ── True peak (absolute safety, mine only) ───────────────────────────
        if (isFinite(mine.truePeak)) {
            if (mine.truePeak > 0.0) {
                push('truepeak', 'major', 'tp_clip', { tp: mine.truePeak.toFixed(1) }, 10 + mine.truePeak);
            } else if (mine.truePeak > -1.0) {
                push('truepeak', 'minor', 'tp_hot', { tp: mine.truePeak.toFixed(1) }, 2);
            }
        }

        // ── Dynamics: PLR relative + absolute ────────────────────────────────
        if (isFinite(mine.plr) && isFinite(tv.plr)) {
            var dPlr = mine.plr - tv.plr; // − => mine more squashed than target
            if (dPlr <= -2) {
                // mine is more compressed than the target.
                var sevD = (mine.plr < 8 || dPlr <= -4) ? 'major' : 'minor';
                push('dynamics', sevD, 'dyn_squashed',
                    { mine: mine.plr.toFixed(1), ref: tv.plr.toFixed(1) }, Math.abs(dPlr) + (mine.plr < 8 ? 3 : 0));
            } else if (dPlr >= 2) {
                // target is denser; mine could take more glue.
                push('dynamics', 'minor', 'dyn_loose',
                    { mine: mine.plr.toFixed(1), ref: tv.plr.toFixed(1) }, dPlr);
            }
        }

        // ── Macro-dynamics (LRA) ─────────────────────────────────────────────
        if (isFinite(mine.lra) && isFinite(tv.lra)) {
            var dLra = Math.abs(mine.lra - tv.lra);
            if (dLra >= 3) {
                push('dynamics', 'minor', 'lra',
                    { mine: mine.lra.toFixed(1), ref: tv.lra.toFixed(1), db: dLra.toFixed(1) }, dLra * 0.5);
            }
        }

        // ── Stereo width (vs target) ─────────────────────────────────────────
        if (mine.stereo && !mine.stereo.isMono && isFinite(tv.width) && tv.width != null) {
            var dW = mine.stereo.width - tv.width;
            if (Math.abs(dW) >= 0.08) {
                push('stereo', 'minor', dW < 0 ? 'width_wider' : 'width_narrower',
                    { mine: mine.stereo.width.toFixed(2), ref: tv.width.toFixed(2) }, Math.abs(dW) * 10);
            }
        } else if (mine.stereo && mine.stereo.isMono && !tv.isMono && tv.width != null) {
            push('stereo', 'minor', 'mine_mono', {}, 3);
        }

        // ── Phase correlation (mine, absolute) ───────────────────────────────
        if (mine.stereo && !mine.stereo.isMono && isFinite(mine.stereo.phaseCorrelation)) {
            var pc = mine.stereo.phaseCorrelation;
            if (pc < 0) {
                push('stereo', 'major', 'phase_neg', { corr: pc.toFixed(2) }, 8 + Math.abs(pc));
            } else if (pc < 0.2) {
                push('stereo', 'minor', 'phase_low', { corr: pc.toFixed(2) }, 3);
            }
        }

        // ── Bass mono compatibility (mine, absolute) ─────────────────────────
        if (isFinite(mine.bassCorr) && mine.channels >= 2) {
            if (mine.bassCorr < 0.5) {
                push('stereo', 'major', 'bass_mono', { corr: mine.bassCorr.toFixed(2) }, 7 + (0.5 - mine.bassCorr));
            } else if (mine.bassCorr < 0.7) {
                push('stereo', 'minor', 'bass_mono_soft', { corr: mine.bassCorr.toFixed(2) }, 2.5);
            }
        }

        // ── Clipping (mine) ──────────────────────────────────────────────────
        if (mine.tech && mine.tech.clipCount >= 8) {
            var heavy = mine.tech.clipPct >= 0.02;
            push('technical', heavy ? 'major' : 'minor', 'clipping',
                { n: mine.tech.clipCount, pct: mine.tech.clipPct.toFixed(3) }, 6 + mine.tech.clipPct);
        }

        // ── DC offset (mine) ─────────────────────────────────────────────────
        // 0.008 ≈ −42 dBFS: below this the offset is audibly/practically harmless
        // (a brickwall limiter alone leaves ~−46 dBFS of asymmetry). Flag only a
        // genuinely headroom-eating offset.
        if (mine.tech && mine.tech.dcMax > 0.008) {
            push('technical', 'minor', 'dc_offset', { v: mine.tech.dcMax.toFixed(4) }, 2);
        }

        // ── Reference looks lossy (info; skip for genre targets, which have no
        //    spectral cutoff to chase) ─────────────────────────────────────────
        if (!tv.isGenre && mine.cutoff && tv.cutoff && tv.cutoff.hz > 0 && mine.cutoff.hz > 0) {
            if (tv.cutoff.hz < mine.cutoff.hz - 2500 && tv.cutoff.hz < 19500) {
                push('technical', 'minor', 'ref_lossy',
                    { khz: (Math.round(tv.cutoff.hz / 100) / 10).toFixed(1) }, 0.5);
            }
        }

        // Rank: major first, then by weight (magnitude of the problem).
        recs.sort(function (a, b) {
            if (SEV[b.severity] !== SEV[a.severity]) return SEV[b.severity] - SEV[a.severity];
            return b.weight - a.weight;
        });

        var major = 0, minor = 0;
        recs.forEach(function (r) { if (r.severity === 'major') major++; else if (r.severity === 'minor') minor++; });
        var verdict = major > 0 ? 'major' : (minor > 0 ? 'minor' : 'close');

        // ── Match % — one 0-100 number (REFERENCE-style "how close am I").
        //   100 = on the target across tone / loudness / dynamics / width; each
        //   axis deducts proportionally, majors (clip/phase/mono-bass) hit hard.
        //   Tuned so a near-match reads ~85-95, a rough one <60.
        var matchPct = 100;
        zoneDeltas.forEach(function (z) { matchPct -= Math.min(8, Math.abs(z.deltaDb)) * 1.4; });
        if (isFinite(mine.lufs) && isFinite(tv.lufs)) matchPct -= Math.min(10, Math.abs(tv.lufs - mine.lufs)) * 0.7;
        if (isFinite(mine.plr) && isFinite(tv.plr)) matchPct -= Math.min(6, Math.abs(mine.plr - tv.plr)) * 1.1;
        if (mine.stereo && !mine.stereo.isMono && tv.width != null && isFinite(tv.width))
            matchPct -= Math.min(0.3, Math.abs(mine.stereo.width - tv.width)) * 25;
        matchPct -= major * 5;
        matchPct = Math.max(0, Math.min(100, Math.round(matchPct)));

        return {
            recommendations: recs,
            zoneDeltas: zoneDeltas,
            bandDeltas: bandDeltas,
            widthFactor: widthFactor,
            isGenre: !!tv.isGenre,
            avgCount: (target && target._avgCount) || 1,
            summary: { major: major, minor: minor, verdict: verdict, matchPct: matchPct }
        };
    }

    window.MixAnalyzer = {
        analyze: analyze,
        compare: compare,
        averageTargets: averageTargets,
        targetView: targetView,
        bassCorrelation: bassCorrelation,
        techStats: techStats,
        BANDS: BANDS,
        ZONES: ZONES,
        GENRE_TARGETS: GENRE_TARGETS,
        version: '1.1.0'
    };
})();
